<template>
  <div class="atc-card">
    <div class="atc-card__head">
      <!-- 指标 -->
      <el-select
        class="atc-select atc-select--metric"
        :value="chart.indicator"
        size="mini"
        @change="val => $emit('change', { indicator: val })"
      >
        <el-option
          v-for="opt in metricOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <i
        class="el-icon-close atc-card__remove"
        :class="{ 'is-disabled': !canRemove }"
        @click="onRemove"
      ></i>
    </div>
    <!-- 对比维度 -->
    <div class="atc-card__dim">
      <el-select
        class="atc-select atc-select--dim"
        :value="chart.dimension"
        size="mini"
        @change="val => $emit('change', { dimension: val })"
      >
        <el-option
          v-for="opt in dimensionOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>
      <!-- 曲线是后端按预估收益从高到低给的 -->
      <span v-if="dimSortHint" class="atc-card__hint">{{ dimSortHint }}</span>
    </div>

    <div class="atc-card__body" v-loading="chart.loading">
      <div v-show="!hasData && !chart.loading" class="atc-card__empty">
        <img src="../../../../assets/img/nodata.png" class="atc-card__empty-img" alt="">
        <p class="atc-card__empty-text">{{ $t('statInfo.noData') }}</p>
      </div>
      <div v-show="hasData" ref="canvas" class="atc-card__canvas"></div>
    </div>

    <ul v-if="hasData" class="atc-legend">
      <li
        v-for="item in legendItems"
        :key="item.name"
        :class="{ 'is-off': !item.on }"
        @click="toggle(item)"
      >
        <i class="atc-legend__mark" :style="markStyle(item)"></i>
        <span class="atc-legend__name">{{ item.name }}</span>
        <span class="atc-legend__value">{{ item.total }}</span>
      </li>
      <!-- 类别多于 5 个才有这个按钮。收起时只留 Top5，展开后多出来的默认不画线 -->
      <li v-if="canExpand" class="atc-legend__more" @click="toggleExpand">
        <span :class="expanded ? 'atc-legend__link' : 'atc-legend__btn'">
          {{ expanded ? $t('statInfo.close') : $t('abroadStat.legendDetail') }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import echarts from 'echarts'
import { MAIN_COLOR, SERIES_COLORS, OTHER_COLOR, metricUnit, formatMetric, formatAxis } from '../../utils/metric'
import { textWidth, yAxisWidth } from '../../utils/chartAxis'
const AXIS_LINE_COLOR = '#e5e6eb'
const SPLIT_LINE_COLOR = '#f0f1f3'
const AXIS_LABEL_COLOR = '#8f959e'
// 浮层里的字号、行距、圆点大小。内容是自己拼的 HTML，样式只能写成内联的
const TIP_TITLE_CSS = 'margin:0;font-size:12px;line-height:20px'
// 一行两栏：名称靠左、数值靠右。浮层宽度是由最长那行撑出来的，行本身是块级会占满，
// 所以 space-between 就能让所有行的数值在同一条右边线上对齐
const TIP_ROW_CSS = 'display:flex;align-items:center;justify-content:space-between;' +
  'margin:6px 0 0;font-size:14px;line-height:14px'
const TIP_DOT_CSS = 'display:inline-block;width:8px;height:8px;margin-right:8px;' +
  'border-radius:50%;vertical-align:middle;background:'
// 两栏之间至少留这么宽，不然最长那行的名称和数值会贴在一起
const TIP_VALUE_CSS = 'margin-left:32px'
// 图例默认只露出后端返回的前 5 条，多的收在「查看详细信息」里
const TOP_N = 5
// 后端返回的类别封顶 10 条，超出的它自己聚合成第 11 条「其他」（顺序也是它排好的）
const MAX_GROUPS = 10

// containLabel 关掉之后（见 draw 里 grid 的注释），横轴标签那行的高度得自己留出来：
// 8 是标签和轴线的间隙（axisLabel.margin 的默认值），12px 的字一行约 17，
// 剩下的 10 是底部留白，和原来 bottom: 10 一致
const X_LABEL_ROOM = 35

export default {
  name: 'AbroadTrendChartCard',
  props: {
    // { id, indicator, dimension, loading, data }，data 是 curve 的原样返回
    chart: {
      type: Object,
      required: true
    },
    metricOptions: {
      type: Array,
      default: () => []
    },
    dimensionOptions: {
      type: Array,
      default: () => []
    },
    // 对比期每个数据点的日期文案，tooltip 里「对比期 2026-08-08」用；没开对比是 null
    compareLabels: {
      type: Array,
      default: null
    },
    // 纵轴留多宽，由父组件按所有图里最宽的标签算出来（见 TrendCharts 的 sharedAxisWidth）。
    // 只会比这张图自己需要的更宽，不传就按自己的算
    axisWidth: {
      type: Number,
      default: 0
    },
    canRemove: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      // 图例是不是展开到全部（Top10 + 其他）
      expanded: false,
      // 这一 tick 里已经排过一次重画了，见 draw
      drawQueued: false,
      selected: {}
    }
  },
  computed: {
    groups() {
      const data = this.chart.data
      return (data && Array.isArray(data.groups)) ? data.groups : []
    },
    labels() {
      const data = this.chart.data
      return (data && Array.isArray(data.labels)) ? data.labels : []
    },
    isTotal() {
      return this.chart.dimension === 'total'
    },
    // 维度下拉旁的说明。合计维度只有一条线（外加对比期那条），没有排序可言，不显示。
    // 维度名直接取下拉里那一项的 label，多语言和口径都跟着页面走，这里不另维护一份
    dimSortHint() {
      if (this.isTotal) return ''
      const hit = this.dimensionOptions.filter(opt => opt.value === this.chart.dimension)[0]
      if (!hit) return ''
      return String(this.$t('abroadStat.dimSortHint')).replace('%s', hit.label)
    },
    hasData() {
      return this.labels.length > 0 && this.groups.length > 0
    },
    // 画图和图例共用一份：[{ name, color, dashed, data, total }]
    series() {
      if (!this.hasData) return []
      if (this.isTotal) {
        const root = this.chart.data || {}
        const main = this.groups[0] || {}
        const list = [{
          name: this.$t('abroadStat.curPeriod'),
          color: MAIN_COLOR,
          dashed: false,
          data: main.data || [],
          total: this.totalText(root.summary)
        }]
        if (Array.isArray(main.cyc_data) && main.cyc_data.length > 0) {
          list.push({
            name: this.$t('abroadStat.cycPeriod'),
            color: MAIN_COLOR,
            dashed: true,
            data: main.cyc_data,
            total: this.totalText(root.cyc_summary)
          })
        }
        return list
      }
      return this.groups.map((group, index) => {
        const other = this.isOtherGroup(group, index)
        return {
          name: other ? this.$t('abroadStat.otherGroup') : this.groupName(group, index),
          color: other ? OTHER_COLOR : SERIES_COLORS[index % SERIES_COLORS.length],
          dashed: false,
          data: group.data || [],
          total: this.totalText(group.summary)
        }
      })
    },
    // 类别多于 5 个才收敛。合计维度最多两条（本期 + 对比期），不会有这个按钮
    canExpand() {
      return this.series.length > TOP_N
    },
    // 每条曲线画不画，key 是曲线名。既给图例的置灰状态用，也直接喂 echarts 的
    // legend.selected —— 一处状态两处用，不会出现「图例亮着线没画」
    selectedMap() {
      const map = {}
      this.series.forEach((item, index) => {
        map[item.name] = this.isOn(item.name, index)
      })
      return map
    },
    // 图例列出来的项：收起时只有 Top5，展开后是全部（Top10 + 其他）
    legendItems() {
      const list = this.expanded ? this.series : this.series.slice(0, TOP_N)
      const map = this.selectedMap
      return list.map(item => ({
        name: item.name,
        color: item.color,
        dashed: item.dashed,
        total: item.total,
        on: !!map[item.name]
      }))
    }
  },
  watch: {
    series() {
      // 指标或维度换了，曲线名对不上了，画不画的记录和展开状态一起回到默认
      this.selected = {}
      this.expanded = false
      this.draw()
    },
    // 别张图换了指标或者取到了新数，共用的纵轴宽度会变，这张图得跟着重画。
    // 它和 series 常常在同一个 tick 里一起变，draw 里有去重，不会画两遍
    axisWidth() {
      this.draw()
    },
    '$i18n.locale'() {
      this.draw()
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    this.draw()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    const inst = this.instance()
    if (inst) inst.dispose()
  },
  methods: {
    instance() {
      if (!this.$refs.canvas) return null
      return echarts.getInstanceByDom(this.$refs.canvas) || echarts.init(this.$refs.canvas)
    },
    // 父组件在张数变化、布局宽度变了之后调
    resize() {
      const inst = this.instance()
      if (inst) inst.resize()
    },
    groupName(group, index) {
      if (group.name) return group.name
      if (group.code !== undefined && group.code !== null && group.code !== '') return String(group.code)
      const hit = this.dimensionOptions.filter(opt => opt.value === this.chart.dimension)[0]
      return (hit ? hit.label : '') + ' ' + (index + 1)
    },
    totalText(total) {
      if (total === undefined || total === null || total === '') return '-'
      return formatMetric(this.chart.indicator, total)
    },
    isOtherGroup(group, index) {
      if (!group) return false
      if (group.code === 'other' || group.name === '其他' || group.name === 'Other') return true
      return this.groups.length > MAX_GROUPS && index === this.groups.length - 1
    },
    isOn(name, index) {
      const hit = this.selected[name]
      return hit === undefined ? index < TOP_N : hit
    },
    toggle(item) {
      this.selected = Object.assign({}, this.selected, { [item.name]: !item.on })
      const inst = this.instance()
      if (inst) inst.dispatchAction({ type: 'legendToggleSelect', name: item.name })
    },
    toggleExpand() {
      if (!this.expanded) {
        // 展开：只是图例多列几行，多出来的那几条本来就没画线，图表不用动
        this.expanded = true
        return
      }
      // 收起：Top5 的点选状态原样留着，多出来的那几条点亮过的要灭掉、记录也抹掉 ——
      const inst = this.instance()
      const map = this.selectedMap
      const keep = {}
      this.series.forEach((sub, index) => {
        if (index < TOP_N) {
          keep[sub.name] = map[sub.name]
          return
        }
        if (map[sub.name] && inst) {
          inst.dispatchAction({ type: 'legendUnSelect', name: sub.name })
        }
      })
      this.selected = keep
      this.expanded = false
    },
    onRemove() {
      if (!this.canRemove) return
      this.$emit('remove')
    },
    markStyle(item) {
      if (!item.dashed) {
        return { background: item.color, color: item.color }
      }
      return {
        color: item.color,
        backgroundImage: 'repeating-linear-gradient(to right, ' +
          item.color + ' 0, ' + item.color + ' 3px, transparent 3px, transparent 5px)'
      }
    },
    draw() {
      if (!this.hasData) return
      if (this.drawQueued) return
      this.drawQueued = true
      this.$nextTick(() => {
        this.drawQueued = false
        const inst = this.instance()
        if (!inst) return
        const that = this
        const key = this.chart.indicator
        const names = this.series.map(item => item.name)
        const isCount = metricUnit(key) === 'count'
        const flatZero = isCount && this.series.every(
          item => (item.data || []).every(val => !Number(val))
        )
        const keepOrder = this.isTotal
        const showSymbol = this.labels.length <= 31
        const firstHalf = Math.ceil(textWidth(String(this.labels[0] || '')) / 2) + 2
        const lastLabel = String(this.labels[this.labels.length - 1] || '')
        const gridRight = Math.max(10, Math.ceil(textWidth(lastLabel) / 2) + 2)
        const gridLeft = Math.max(
          this.axisWidth,
          yAxisWidth(this.chart.indicator, this.chart.data),
          firstHalf
        )
        // notMerge：换指标/维度后曲线条数会变，合并式 setOption 去不掉旧的
        inst.setOption({
          animationDurationUpdate: 0,
          color: this.series.map(item => item.color),
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line' },
            confine: true,
            // echarts 默认是 rgba(50,50,50,0.7)，底下的曲线和网格线全透上来了，压深一点
            backgroundColor: 'rgba(38, 42, 51, 0.92)',
            borderWidth: 0,
            // 默认只有 5px，文字快贴到边框上
            padding: [10, 14],
            textStyle: { color: '#fff', fontSize: 12 },
            extraCssText: 'border-radius:6px;box-shadow:0 4px 16px rgba(31, 35, 41, 0.16)',
            formatter(params) {
              const list = (params || []).slice()
              // 合计维度保持 series 的顺序（本期、对比期），其余按数值降序
              if (!keepOrder) {
                list.sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
              }
              let res = '<p style="' + TIP_TITLE_CSS + '">' + list[0].axisValue + '</p>'
              for (let i = 0; i < list.length; i++) {
                // 圆点自己拼，不用 params.marker —— 它固定 10px，太抢眼。
                // 名称和数值各占一栏，中间不写「：」，靠两端对齐分开
                res += '<p style="' + TIP_ROW_CSS + '">' +
                  '<span><span style="' + TIP_DOT_CSS + list[i].color + '"></span>' +
                  that.tipName(list[i]) + '</span>' +
                  '<span style="' + TIP_VALUE_CSS + '">' + formatMetric(key, list[i].value) + '</span>' +
                  '</p>'
              }
              return res
            }
          },
          // 自己画图例，但画哪几条线是靠 legend.selected 控的，所以组件得留着
          legend: { show: false, data: names, selected: this.selectedMap },
          grid: { left: gridLeft, right: gridRight, top: 20, bottom: X_LABEL_ROOM, containLabel: false },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: this.labels,
            axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
            axisTick: { show: false },
            axisLabel: { color: AXIS_LABEL_COLOR, showMinLabel: true, showMaxLabel: true }
          },
          yAxis: {
            type: 'value',
            // 整数指标不许出小数刻度
            minInterval: isCount ? 1 : 0,
            max: flatZero ? 5 : null,
            interval: flatZero ? 1 : null,
            // 纵轴不画轴线也不画刻度，读数靠横向网格线
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: SPLIT_LINE_COLOR } },
            axisLabel: {
              color: AXIS_LABEL_COLOR,
              // 轴上写紧凑版（500K / 5M），tooltip 里才是精确值
              formatter: val => formatAxis(key, val)
            }
          },
          series: this.series.map(item => ({
            name: item.name,
            type: 'line',
            smooth: true,
            symbol: 'emptyCircle',
            symbolSize: 6,
            showSymbol: showSymbol,
            hoverAnimation: false,
            lineStyle: item.dashed ? { type: 'dashed' } : { type: 'solid' },
            data: item.data
          }))
        }, true)
        inst.resize()
      })
    },
    tipName(param) {
      if (param.seriesName !== this.$t('abroadStat.cycPeriod')) return param.seriesName
      const hit = (this.compareLabels || [])[param.dataIndex]
      return hit ? param.seriesName + ' ' + hit : param.seriesName
    }
  }
}
</script>

<style lang="scss" scoped>
$main: #3361ff;
$text: #1f2329;
$text-sub: #4e5969;
$text-weak: #8f959e;
$line: #f0f1f3;

.atc-card {
  padding: 16px 20px 12px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;

  &:hover .atc-card__remove {
    visibility: visible;
  }
}
.atc-card__head {
  display: flex;
  align-items: center;
}
.atc-card__remove {
  flex: 0 0 auto;
  visibility: hidden;
  margin-left: auto;
  padding: 2px;
  font-size: 15px;
  color: #c0c4cc;
  cursor: pointer;

  &:hover {
    color: $main;
  }
  /* 只剩一张图时不许删 */
  &.is-disabled,
  &.is-disabled:hover {
    color: #e4e7ed;
    cursor: not-allowed;
  }
}
.atc-card__body {
  position: relative;
  height: 260px;
  margin-top: 10px;
}
.atc-card__canvas {
  width: 100%;
  height: 100%;
}
.atc-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}
/* 全局 .nodata 那套是 320*230，卡片只有 260px 高，按原图比例缩到一半 */
.atc-card__empty-img {
  display: block;
  width: 132px;
}
.atc-card__empty-text {
  margin: 8px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.35);
}

/* 指标下拉当标题用：去掉边框、字号加大加粗 */
.atc-select--metric {
  width: 130px;

  /deep/ .el-input__inner {
    height: 24px;
    line-height: 24px;
    padding-left: 0;
    padding-right: 20px;
    border: none;
    background: transparent;
    font-size: 15px;
    font-weight: 600;
    color: $text;
  }
  /deep/ .el-input__icon {
    line-height: 24px;
  }
}
/* 维度下拉和它旁边的说明同一行 */
.atc-card__dim {
  display: flex;
  align-items: center;
  margin-top: 6px;
}
/* 维度下拉是常规的小号选择器 */
.atc-select--dim {
  flex: 0 0 auto;
  width: 104px;

  /deep/ .el-input__inner {
    color: $text-sub;
  }
}
/* 说明文案。三张一行时卡片只有 1/3 宽，放不下就省略号，别把下拉挤变形 */
.atc-card__hint {
  flex: 1;
  min-width: 0;
  margin-left: 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  color: $text-weak;
}

.atc-legend {
  display: flex;
  flex-wrap: wrap;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    margin: 6px 16px 0 0;
    font-size: 12px;
    cursor: pointer;

    /* 被点掉的曲线，整项置灰 */
    &.is-off {
      opacity: 0.4;
    }
  }
}
.atc-legend__mark {
  position: relative;
  flex: 0 0 auto;
  width: 18px;
  height: 2px;
  margin-right: 6px;
  /* 实线是纯色底，虚线是 markStyle 里给的渐变，两者粗细一致 */
  background-repeat: repeat-x;

  /* 中间那个空心圆，和线上的数据点长一样：白底 + 2px 描边。
     描边色取 currentColor —— markStyle 把曲线颜色同时写进了 color */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 4px;
    height: 4px;
    margin: -4px 0 0 -4px;
    border: 2px solid currentColor;
    border-radius: 50%;
    background: #fff;
  }
}
.atc-legend__name {
  color: $text-sub;
}
.atc-legend__value {
  margin-left: 6px;
  font-weight: 600;
  color: $text;
}

/* 「查看详细信息」/「收起」：跟在图例后面，不是图例项，所以没有前面那截线 */
.atc-legend__more {
  white-space: nowrap;
}
/* 收起状态是个描边小按钮，展开后换成一个蓝色文字链，跟原型一致 */
.atc-legend__btn {
  padding: 2px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: $text-sub;

  &:hover {
    border-color: $main;
    color: $main;
  }
}
.atc-legend__link {
  color: $main;
}
</style>
